import { useMutation } from '@apollo/client';
import type { AddControlParentsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { AddControlParentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useCreateControlParentsTRPC } from './useCreateControlParentsTRPC';

export const useCreateControlParents = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [createControlParentsGraphQL, graphqlState] = useMutation(
    AddControlParentsDocument,
    {
      update: (cache) => {
        evictField(cache, 'control');
        evictField(cache, 'control_group');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useCreateControlParentsTRPC();

  const createControlParents = async (
    objects: { ControlId: string; ParentId: string }[]
  ): Promise<AddControlParentsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.createControlParents(objects);
    }

    const result = await createControlParentsGraphQL({
      variables: { objects },
    });
    if (!result.data) {
      throw new Error('Failed to create control parents');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      createControlParents,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    createControlParents,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
